import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { HiBell, HiXMark, HiCheck, HiTrash } from 'react-icons/hi2';
// Simple date formatter function
const formatDistanceToNow = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
  }
  return 'just now';
};

const NotificationDropdown = ({ onClose }) => {
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotification();

  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const autoReadTimerRef = useRef(null);
  const hasAutoMarkedRef = useRef(false);

  useEffect(() => {
    // Fetch only unread notifications (limit 5 for dropdown)
    fetchNotifications({ limit: 5, unreadOnly: true });
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Auto-mark unread notifications as read after dropdown stays open briefly
  useEffect(() => {
    const hasUnread = notifications.some((notif) => !notif.isRead);

    if (loading || !hasUnread || hasAutoMarkedRef.current) {
      return;
    }

    autoReadTimerRef.current = setTimeout(async () => {
      hasAutoMarkedRef.current = true;
      await markAllAsRead(false);
    }, 3000);

    return () => {
      if (autoReadTimerRef.current) {
        clearTimeout(autoReadTimerRef.current);
      }
    };
  }, [notifications, loading, markAllAsRead]);

  // Extract order ID from cashback notification message
  const extractOrderId = (message) => {
    const match = message.match(/order #([A-Za-z0-9]+)/i);
    return match ? match[1] : null;
  };

  // Extract cashback amount from notification message
  const extractCashbackAmount = (message) => {
    const match = message.match(/₹([\d,]+\.?\d*)/);
    return match ? match[1] : null;
  };

  // Extract referral level from notification message
  const extractReferralLevel = (message) => {
    const match = message.match(/Level (\d+)/i);
    return match ? parseInt(match[1]) : null;
  };

  // Extract referral reward amount from notification message
  const extractReferralAmount = (message) => {
    const match = message.match(/₹([\d,]+\.?\d*)/);
    return match ? match[1] : null;
  };

  // Extract order ID from referral notification message
  const extractReferralOrderId = (message) => {
    const match = message.match(/order #([A-Za-z0-9]+)/i);
    return match ? match[1] : null;
  };

  const handleNotificationClick = (notification) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    
    // Navigate to order details if cashback notification
    if (notification.type === 'cashback') {
      const orderId = extractOrderId(notification.message);
      if (orderId) {
        onClose();
        navigate(`/order/${orderId}`);
        return;
      }
    }
    
    // Navigate to order or referral dashboard if referral notification
    if (notification.type === 'referral') {
      const orderId = extractReferralOrderId(notification.message);
      if (orderId) {
        onClose();
        navigate(`/order/${orderId}`);
        return;
      } else {
        onClose();
        navigate('/referral-dashboard');
        return;
      }
    }
    
    // Close dropdown
    onClose();
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return '📦';
      case 'cashback':
        return '💰';
      case 'referral':
        return '🎁';
      case 'discount':
        return '🎉';
      case 'cartReminder':
        return '🛒';
      default:
        return '🔔';
    }
  };

  // Filter to show only unread notifications
  const unreadNotifications = notifications.filter(notif => !notif.isRead);
  const displayedNotifications = showAll ? unreadNotifications : unreadNotifications.slice(0, 5);

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 max-h-[500px] flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-yellow-50">
        <div className="flex items-center gap-2">
          <HiBell className="text-2xl text-orange-500" />
          <h3 className="font-bold text-gray-900">Notifications</h3>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <HiXMark className="text-xl text-gray-600" />
        </button>
      </div>

      {/* Actions */}
      {unreadCount > 0 && (
        <div className="px-4 py-2 border-b border-gray-200 flex gap-2">
          <button
            onClick={markAllAsRead}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <HiCheck className="text-base" />
            Mark all as read
          </button>
        </div>
      )}

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {loading && unreadNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
            Loading notifications...
          </div>
        ) : displayedNotifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <HiBell className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="font-medium">No notifications</p>
            <p className="text-sm">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedNotifications.map((notification) => {
              const isCashback = notification.type === 'cashback';
              const isReferral = notification.type === 'referral';
              const cashbackAmount = isCashback ? extractCashbackAmount(notification.message) : null;
              const referralAmount = isReferral ? extractReferralAmount(notification.message) : null;
              const referralLevel = isReferral ? extractReferralLevel(notification.message) : null;
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.isRead 
                      ? isCashback 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500'
                        : isReferral
                        ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500'
                        : 'bg-blue-50/50'
                      : isCashback
                        ? 'bg-green-50/30 border-l-4 border-green-300'
                        : isReferral
                        ? 'bg-purple-50/30 border-l-4 border-purple-300'
                        : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`text-2xl flex-shrink-0 ${isCashback || isReferral ? 'animate-pulse' : ''}`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isCashback && cashbackAmount && (
                        <div className="mb-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">
                            💰 ₹{cashbackAmount}
                          </span>
                        </div>
                      )}
                      {isReferral && referralLevel && referralAmount && (
                        <div className="mb-1 flex flex-wrap gap-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-bold">
                            Level {referralLevel}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs font-bold">
                            ₹{referralAmount}
                          </span>
                        </div>
                      )}
                      <p
                        className={`text-sm ${
                          !notification.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDistanceToNow(new Date(notification.createdAt))}
                      </p>
                      {(isCashback || isReferral) && (
                        <p className={`text-xs font-medium mt-1 ${
                          isCashback ? 'text-green-600' : 'text-purple-600'
                        }`}>
                          Click to {isCashback ? 'view order' : 'view details'} →
                        </p>
                      )}
                    </div>
                    {!notification.isRead && (
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${
                        isCashback ? 'bg-green-500' : isReferral ? 'bg-purple-500' : 'bg-blue-500'
                      }`}></div>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <HiTrash className="text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer - Show if there are unread notifications */}
      {unreadNotifications.length > 0 && (
        <div className="border-t border-gray-200 p-3 bg-gray-50">
          <button
            onClick={handleViewAll}
            className="w-full text-center text-orange-600 font-semibold hover:text-orange-700 transition-colors py-2"
          >
            View All Notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;

